import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, ArrowUpRightIcon, MailIcon, NetworkIcon, GraduationCapIcon, CalendarCheckIcon, MegaphoneIcon, HandshakeIcon, LightbulbIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/b8PVJjNvrAhoquF5QgjoRy/HAA_National_Network_map_25_percent_stronger.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const BLUE = '#1565C0';
const RED = '#cf142b';
const BENEFITS = [{
  icon: NetworkIcon,
  title: 'Access to Our National Network',
  titleEs: 'Acceso a Nuestra Red Nacional',
  text: 'Direct connection to Honduran-American organizations, leaders, professionals, and institutions in cities across the United States — and to the partners and allies who work alongside them.',
  textEs: 'Conexión directa con organizaciones, líderes, profesionales e instituciones hondureño-americanas en ciudades de todo Estados Unidos, y con los socios y aliados que trabajan junto a ellas.'
}, {
  icon: GraduationCapIcon,
  title: 'Specialized Trainings',
  titleEs: 'Capacitaciones Especializadas',
  text: 'Practical tools for running an effective organization: governance and board development, nonprofit compliance, fundraising, financial management, storytelling, and volunteer engagement.',
  textEs: 'Herramientas prácticas para dirigir una organización efectiva: gobernanza y desarrollo de juntas directivas, cumplimiento normativo, recaudación de fondos, gestión financiera, narrativa y participación de voluntarios.'
}, {
  icon: CalendarCheckIcon,
  title: 'Curated Events',
  titleEs: 'Eventos Curados',
  text: 'Invitations to specially curated business, educational, cultural, and civic events — receptions, convenings, and delegations where relationships and opportunities are built.',
  textEs: 'Invitaciones a eventos empresariales, educativos, culturales y cívicos especialmente curados: recepciones, encuentros y delegaciones donde se construyen relaciones y oportunidades.'
}, {
  icon: MegaphoneIcon,
  title: 'National Visibility',
  titleEs: 'Visibilidad Nacional',
  text: 'Your organization featured across our national platform, media relationships, and communications — so the work you do locally is seen and understood nationally.',
  textEs: 'Su organización destacada en nuestra plataforma nacional, relaciones con medios y comunicaciones, para que el trabajo que realiza localmente sea visto y comprendido a nivel nacional.'
}, {
  icon: HandshakeIcon,
  title: 'Partnership & Funding Pathways',
  titleEs: 'Vías de Alianza y Financiamiento',
  text: 'Introductions to corporate, institutional, and philanthropic partners, plus shared opportunities for joint programming and collaborative grant applications.',
  textEs: 'Presentaciones a socios corporativos, institucionales y filantrópicos, además de oportunidades compartidas de programación conjunta y solicitudes de subvenciones colaborativas.'
}, {
  icon: LightbulbIcon,
  title: 'A Shared Voice on Policy',
  titleEs: 'Una Voz Compartida en Políticas',
  text: 'A collective platform for the issues affecting Honduran-American families, with coordinated advocacy that carries further than any single organization can reach alone.',
  textEs: 'Una plataforma colectiva para los asuntos que afectan a las familias hondureño-americanas, con defensa coordinada que llega más lejos de lo que cualquier organización puede alcanzar por sí sola.'
}];
const EASE = [0.16, 1, 0.3, 1] as const;
const GRID_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(to right, rgba(11,31,63,0.09) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,63,0.09) 1px, transparent 1px)',
  backgroundSize: '64px 64px',
  maskImage: 'radial-gradient(ellipse at 50% 40%, black 25%, transparent 76%)',
  WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 25%, transparent 76%)'
};
export function NationalNetworkPage() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  return <div className="min-h-screen w-full bg-white text-[#0b1f3f] font-nunito antialiased" data-id="element-3707" data-mp="2xe1l94wt6sc9">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full backdrop-blur-md bg-white/90 border-b border-[#0b1f3f]/10" data-id="element-3708" data-mp="31ell4g8roa23">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between" data-id="element-3709" data-mp="131v0bee44x6r">
          <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association National Network" data-id="element-3710" data-mp="172duhf3hafc4">
            <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-11 sm:h-12 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3711" data-mp="ure44x1j4umo" />
          </a>
          <a href="#" className="inline-flex items-center gap-2 text-[#0b1f3f]/55 hover:text-[#cf142b] text-xs font-semibold uppercase tracking-[0.16em] transition-colors" data-id="element-3712" data-mp="lov7iuoc7e8v">
            <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3713" data-mp="1dgb33e7vgbb8" />
            {isEs ? 'Volver' : 'Back'}
          </a>
        </div>
        {/* Flag rule */}
        <div className="h-[3px] w-full flex" data-id="element-3714" data-mp="14ge7esn2ec6o">
          <div className="flex-1" style={{
          backgroundColor: BLUE
        }} data-id="element-3715" data-mp="14jk0eiytdy7o" />
          <div className="flex-1 bg-white" data-id="element-3716" data-mp="3u7jaf6l8sklz" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3717" data-mp="3p7ohyyltjmyt" />
        </div>
      </header>

      <main data-id="element-3718" data-mp="3qou5l1zn2rt3">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden bg-[#f7f9fc]" data-id="element-3719" data-mp="3qpvhf8ohbibv">
          <div aria-hidden="true" className="absolute inset-0" style={GRID_STYLE} data-id="element-3720" data-mp="3vnonm1ps8cc5" />

          <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-24 lg:pb-20" data-id="element-3721" data-mp="3vlki5fc8j59r">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} className="max-w-4xl" data-id="element-3722" data-mp="3lpr2hn37yvnr">
              <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="w-[clamp(17rem,42vw,26rem)] h-auto object-contain mb-9 -ml-3" data-id="element-3723" data-mp="2y1zo8dy4c8ju" />
              <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.32em] mb-6" style={{
              color: BLUE
            }} data-id="element-3724" data-mp="2tyl69hqmdi3r">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3725" data-mp="2adkqambkhw66" />
                {isEs ? 'Una Diáspora · Una Voz Colectiva' : 'One Diaspora · One Collective Voice'}
              </p>
              <h1 className="font-bebas text-5xl sm:text-6xl lg:text-[4.75rem] tracking-[0.02em] leading-[0.92] mb-7" data-id="element-3726" data-mp="2fb2vq2qdlt9f">
                {isEs ? <>
                    Organizaciones Separadas.
                    <br data-id="element-3727" data-mp="2ocamlzqkbrth" />
                    <span style={{
                  color: RED
                }} data-id="element-3728" data-mp="331gsg6jwogac">Un Movimiento Nacional.</span>
                  </> : <>
                    Separate Organizations.
                    <br data-id="element-3729" data-mp="2y4q30sm7o8c9" />
                    <span style={{
                  color: RED
                }} data-id="element-3730" data-mp="3c1ve570s7xed">One National Movement.</span>
                  </>}
              </h1>
              <p className="font-nunito text-[#0b1f3f]/70 text-lg sm:text-xl leading-relaxed max-w-2xl mb-9" data-id="element-3731" data-mp="374d41db13c4s">
                {isEs ? 'Nuestras organizaciones hermanas trabajan en ciudades de todo Estados Unidos: educando, sanando, defendiendo y celebrando. Por separado, cada una transforma su comunidad. Juntas, se convierten en una fuerza nacional.' : 'Our sister organizations work in cities across the United States — educating, healing, advocating, and celebrating. Apart, each one transforms its community. Together, they become a national force.'}
              </p>
              <a href={mailto(isEs ? 'Solicitud — Red Nacional Hondureño-Americana' : 'Inquiry — Honduran-American National Network')} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = NAVY;
            }} data-id="element-3732" data-mp="3lqo7i7ai29l">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3733" data-mp="2vhl3tcaw0ato" />
              </a>
            </motion.div>
          </div>

          {/* Stat rail */}
          <div className="relative border-t border-[#0b1f3f]/10 bg-white" data-id="element-3734" data-mp="2lpffccjugbj8">
            <dl className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-3 divide-x divide-[#0b1f3f]/10" data-id="element-3735" data-mp="2gsyq1f0wrr4k">
              {[{
              value: `${SISTER_ORGANIZATIONS.length}`,
              label: isEs ? 'Organizaciones' : 'Organizations'
            }, {
              value: '6',
              label: isEs ? 'Beneficios' : 'Member Benefits'
            }, {
              value: '1',
              label: isEs ? 'Red Nacional' : 'National Network'
            }].map((stat, i) => <div key={stat.label} className={i === 0 ? 'pr-6 py-7' : 'px-6 py-7'} data-id="element-3736" data-mp="re6y9ogrik0i" data-mpi={i}>
                  <dd className="font-bebas text-4xl sm:text-5xl tracking-wide leading-none mb-2" data-id="element-3737" data-mp="mi8d09qqdi4b">
                    {stat.value}
                  </dd>
                  <dt className="font-nunito text-[#0b1f3f]/45 text-[10px] font-bold uppercase tracking-[0.2em]" data-id="element-3738" data-mp="3pxxel2zsy6x9">
                    {stat.label}
                  </dt>
                </div>)}
            </dl>
          </div>
        </section>

        {/* 2 — What we offer */}
        <section className="border-t border-[#0b1f3f]/10 bg-white" data-id="element-3739" data-mp="1k34dtjtla2pk">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3740" data-mp="1f4t3z93ans7c">
            <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-12 lg:gap-16" data-id="element-3741" data-mp="1f6xalyskv5bb">
              <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-3742" data-mp="1mqomv7i94gh9">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.32em] mb-6" style={{
                color: BLUE
              }} data-id="element-3743" data-mp="1rozvxg0q2myf">
                  {isEs ? '01 — Qué Ofrecemos' : '01 — What We Offer'}
                </p>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" data-id="element-3744" data-mp="25ebiajyz1wrs">
                  {isEs ? 'La Membresía No Es Simbólica' : 'Membership Is Not Symbolic'}
                </h2>
                <div className="w-14 h-[3px] mb-6" style={{
                backgroundColor: RED
              }} data-id="element-3745" data-mp="1wkf883j30asq" />
                <p className="font-nunito text-[#0b1f3f]/65 text-base leading-relaxed" data-id="element-3746" data-mp="2od5v3nevkn5b">
                  {isEs ? 'Es acceso, preparación y presencia en las salas donde se toman decisiones.' : 'It is access, preparation, and presence in the rooms where decisions get made.'}
                </p>
              </div>

              <ul className="divide-y divide-[#0b1f3f]/10 border-t border-[#0b1f3f]/10" data-id="element-3747" data-mp="2yzkkq1i7rprk">
                {BENEFITS.map((benefit, i) => {
                const Icon = benefit.icon;
                return <motion.li key={benefit.title} initial={{
                  opacity: 0,
                  y: 16
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true,
                  margin: '-60px'
                }} transition={{
                  duration: 0.45,
                  delay: Math.min(i, 3) * 0.06,
                  ease: EASE
                }} className="py-7 flex gap-6" data-id="element-3748" data-mp="2p2zh9kluempu" data-mpi={i}>
                      <div className="flex-shrink-0 flex flex-col items-center gap-3 pt-1" data-id="element-3749" data-mp="1r6u7x7jt2b3i">
                        <span className="font-nunito text-[#0b1f3f]/30 text-[10px] font-bold tabular-nums tracking-[0.1em]" data-id="element-3750" data-mp="1w6gitac5vf95">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <Icon className="w-4 h-4" style={{
                      color: BLUE
                    }} data-id="element-3751" data-mp="13i1ebhqnofc2" />
                      </div>
                      <div data-id="element-3752" data-mp="18ft8yjioqfjy">
                        <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-2.5" data-id="element-3753" data-mp="18h4asdskld8f">
                          {isEs ? benefit.titleEs : benefit.title}
                        </h3>
                        <p className="font-nunito text-[#0b1f3f]/65 text-sm leading-relaxed" data-id="element-3754" data-mp="1i77bl05xnvhb">
                          {isEs ? benefit.textEs : benefit.text}
                        </p>
                      </div>
                    </motion.li>;
              })}
              </ul>
            </div>
          </div>
        </section>

        {/* 3 — Map */}
        <section className="border-t border-[#0b1f3f]/10 bg-[#f7f9fc]" data-id="element-3755" data-mp="2mgz2hf29i6qu">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3756" data-mp="3153niposeucs">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3757" data-mp="316wwibmvhqny">
              <div className="max-w-xl" data-id="element-3758" data-mp="2wd2fjfynjpsk">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.32em] mb-6" style={{
                color: BLUE
              }} data-id="element-3759" data-mp="3a58vb0xw7urh">
                  {isEs ? '02 — La Red' : '02 — The Network'}
                </p>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" data-id="element-3760" data-mp="3k36fndcmt7et">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-[#0b1f3f]/45 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3761" data-mp="1ps18w0690ev9">
                {isEs ? 'Seleccione un punto para ver la organización' : 'Select a point to view the organization'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-[#0b1f3f]/12 bg-white shadow-sm" data-id="element-3762" data-mp="59z1tx91xq3p">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#0b1f3f]/10" data-id="element-3763" data-mp="3mmb8jgk8cbdn">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3764" data-mp="3rle2a4urev6b" />
                <span className="font-nunito text-[#0b1f3f]/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3765" data-mp="3in2h4icnwpl9">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              <div className="h-[440px] sm:h-[560px] w-full" data-id="element-3766" data-mp="38po30pe7ocw4">
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-[#f4f6fa]" data-id="element-3768" data-mp="11yyahddjstwf">
                      <p className="font-nunito text-[#0b1f3f]/40 text-xs uppercase tracking-[0.2em]" data-id="element-3769" data-mp="16wgm5biy9n84">
                        {isEs ? 'Cargando mapa…' : 'Loading map…'}
                      </p>
                    </div>} data-id="element-3767" data-mp="11yesjwnwe5ie">
                  <NetworkMap isEs={isEs} data-id="element-3770" data-mp="vh4w8pfeva8g" />
                </Suspense>
              </div>
            </div>
          </div>
        </section>

        {/* 4 — Roster */}
        <section className="border-t border-[#0b1f3f]/10 bg-white" data-id="element-3771" data-mp="rio0f21fxm6c">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3772" data-mp="1o62ggy36nh3u">
            <div className="max-w-2xl mb-12" data-id="element-3773" data-mp="1j8shaqpzrr2t">
              <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.32em] mb-6" style={{
              color: BLUE
            }} data-id="element-3774" data-mp="1egr8jes18ihj">
                {isEs ? '03 — Organizaciones Hermanas' : '03 — Sister Organizations'}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" data-id="element-3775" data-mp="226d3tr5aadk9">
                {isEs ? 'Quiénes Forman la Red' : 'Who Makes Up the Network'}
              </h2>
              <p className="font-nunito text-[#0b1f3f]/65 text-base leading-relaxed" data-id="element-3776" data-mp="3ps6cmz7tb6ar">
                {isEs ? 'Cada organización aporta una fortaleza distinta: educación, salud mental, artes, defensa legal, desarrollo juvenil. Esta es la base de la red y continúa creciendo.' : 'Each organization brings a distinct strength — education, mental health, the arts, legal advocacy, youth development. This is the foundation of the network, and it continues to grow.'}
              </p>
            </div>

            <ul className="border-t border-[#0b1f3f]/10" data-id="element-3777" data-mp="hdfqj2a0ifed">
              {SISTER_ORGANIZATIONS.map((org, i) => <motion.li key={org.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="border-b border-[#0b1f3f]/10 transition-colors duration-200 hover:bg-[#f7f9fc]" data-id="element-3778" data-mp="ldgdxcf3a0xk" data-mpi={i}>
                  <div className="py-8 grid lg:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-8 gap-y-4 items-start px-2 sm:px-4" data-id="element-3779" data-mp="3joepi7ojzt4f">
                    <span className="font-nunito text-[#0b1f3f]/30 text-[10px] font-bold tabular-nums tracking-[0.1em] pt-2" data-id="element-3780" data-mp="3sg6wu9ia6m4s">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-3781" data-mp="2zznuuj9r8cok">
                      <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-2.5" style={{
                    color: BLUE
                  }} data-id="element-3782" data-mp="2zyukz9fi1727">
                        {isEs ? org.focusEs : org.focus}
                      </p>
                      <h3 className="font-bebas text-2xl sm:text-[1.7rem] tracking-[0.03em] leading-tight mb-2" data-id="element-3783" data-mp="2qlc9q2ramqya">
                        {org.name}
                      </h3>
                      <p className="inline-flex items-center gap-1.5 font-nunito text-[#0b1f3f]/50 text-xs uppercase tracking-[0.12em]" data-id="element-3784" data-mp="2gvan7fel9glh">
                        <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                      color: RED
                    }} data-id="element-3785" data-mp="34lwv96olf0vh" />
                        {org.location}
                      </p>
                    </div>
                    <p className="font-nunito text-[#0b1f3f]/65 text-sm leading-relaxed" data-id="element-3786" data-mp="2zlsa33cqphyx">
                      {isEs ? org.descriptionEs : org.description}
                    </p>
                    <div className="flex flex-col gap-2.5 lg:items-end lg:text-right" data-id="element-3787" data-mp="1j9mx1c8q7uie">
                      {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors whitespace-nowrap hover:opacity-70" style={{
                    color: RED
                  }} data-id="element-3788" data-mp="o259h3cobiin">
                          {isEs ? 'Sitio web' : 'Website'}
                          <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3789" data-mp="59kxcxl71s1j" />
                        </a> : null}
                      <a href={mailto(`${isEs ? 'Contacto' : 'Contact'} — ${org.name}`)} className="inline-flex items-center gap-1.5 font-nunito text-[#0b1f3f]/50 text-[11px] font-bold uppercase tracking-[0.16em] hover:text-[#0b1f3f] transition-colors whitespace-nowrap" data-id="element-3790" data-mp="3sgn5ts6wh0yw">
                        <MailIcon className="w-3.5 h-3.5" data-id="element-3791" data-mp="33up98omhkt1x" />
                        {isEs ? 'Contactar' : 'Contact'}
                      </a>
                    </div>
                  </div>
                </motion.li>)}
            </ul>
          </div>
        </section>

        {/* 5 — CTA */}
        <section className="relative overflow-hidden text-white" style={{
        backgroundColor: NAVY
      }} data-id="element-3792" data-mp="38u25fzz8zch6">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.14]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.16) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.16) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 50%, black 20%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 50%, black 20%, transparent 72%)'
        }} data-id="element-3793" data-mp="3msialmhgoux7" />
          <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3794" data-mp="cimgoddv9oh9">
            <h2 className="font-bebas text-5xl sm:text-6xl tracking-[0.02em] leading-[0.92] mb-7" data-id="element-3795" data-mp="hge9d9mc5emi">
              {isEs ? <>
                  Su Organización
                  <br data-id="element-3796" data-mp="r6h9ruvsduuv" />
                  <span className="text-white/45" data-id="element-3797" data-mp="r70gsgjweyu4">Pertenece Aquí</span>
                </> : <>
                  Your Organization
                  <br data-id="element-3798" data-mp="105cfz6ytz4y6" />
                  <span className="text-white/45" data-id="element-3799" data-mp="104szluotwom9">Belongs Here</span>
                </>}
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-8" data-id="element-3800" data-mp="153dvxj0ymo5f">
              {isEs ? 'Si su organización sirve a la comunidad hondureña o hondureño-americana —en cualquier ciudad, de cualquier tamaño— queremos conocerla.' : 'If your organization serves the Honduran or Honduran-American community — in any city, at any size — we want to know you.'}
            </p>
            <p className="font-nunito text-[11px] font-bold uppercase tracking-[0.32em] mb-10 text-white" data-id="element-3801" data-mp="1oabwunicyrun">
              {isEs ? 'Conectar · Empoderar · Celebrar' : 'Connect · Empower · Celebrate'}
            </p>
            <a href={mailto(isEs ? 'Membresía — Red Nacional Hondureño-Americana' : 'Membership — Honduran-American National Network')} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors hover:text-white" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3802" data-mp="1fayqi3pj6je5">
              <MailIcon className="w-4 h-4" data-id="element-3803" data-mp="2f8xw32728v65" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white border-t border-[#0b1f3f]/10 py-8" data-id="element-3804" data-mp="21ews9u194hyx">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3805" data-mp="26gcdbm3fiohb">
          <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-11 w-auto object-contain" data-id="element-3806" data-mp="2u3argd42tkc2" />
          <p className="font-nunito text-[#0b1f3f]/45 text-[10px] uppercase tracking-[0.22em]" data-id="element-3807" data-mp="2k6fva0v9zw0y">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}